/**
 * Key Bindings → Gamepad: what each shoulder and trigger fires (controls plan C1, the next program's
 * E5). Each button binds to any bar's slot or a page flip; a bumper can be chosen as the controller's
 * Shift, which then fires nothing itself and, held, switches the other buttons to their shifted column
 * (bar 2 by default). Saved in UserPrefs `controls`; InputSystem reads it through gamepadBindings.ts.
 */
import React, { useEffect, useState } from 'react';
import { TomeButton } from '../../primitives/tome';
import { storage } from '../../../services/StorageService';
import { USER_PREFS_DEFAULTS } from '../../../../shared/userprefs/schema';
import { MAX_BARS, MAX_PAGES_PER_BAR, SLOTS_PER_PAGE } from '../../../../shared/hotbar/barLayout';
import { pageAction, slotAction } from '../../../../shared/hotbar/slotActions';
import { PAD_ABILITY_BUTTONS, controllerBindingPrefs, type PadAbilityButton, type PadBindingPrefs } from '../../../systems/input/gamepadBindings';

export const PAD_BUTTON_LABELS: Readonly<Record<PadAbilityButton, string>> = {
  ability1: 'Left Bumper (LB)', ability2: 'Right Bumper (RB)', ability3: 'Left Trigger (LT)', ability4: 'Right Trigger (RT)',
};

const PAD_BUTTON_SHORT: Readonly<Record<PadAbilityButton, string>> = { ability1: 'LB', ability2: 'RB', ability3: 'LT', ability4: 'RT' };

/** Every bindable action, bar by bar: its slots, then its page flips. */
const BAR_OPTIONS = Array.from({ length: MAX_BARS }, (_, i) => {
  const bar = i + 1;
  return {
    bar,
    actions: [
      ...Array.from({ length: SLOTS_PER_PAGE }, (_, s) => ({ value: slotAction(bar, s + 1), label: `Bar ${bar} · slot ${s + 1}` })),
      { value: pageAction(bar, 'next'), label: `Bar ${bar} · next page` },
      { value: pageAction(bar, 'prev'), label: `Bar ${bar} · previous page` },
      ...Array.from({ length: MAX_PAGES_PER_BAR }, (_, p) => ({ value: pageAction(bar, p + 1), label: `Bar ${bar} · page ${p + 1}` })),
    ],
  };
});

function ActionSelect({ value, onChange, label, testId }: { value: string; onChange: (v: string) => void; label: string; testId: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testId}>
      {BAR_OPTIONS.map((group) => (
        <optgroup key={group.bar} label={`Bar ${group.bar}`}>
          {group.actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export const ControllerBindingsPanel: React.FC = () => {
  const [prefs, setPrefs] = useState<PadBindingPrefs>(() => controllerBindingPrefs());
  useEffect(() => storage.subscribeUserPrefs((p) => setPrefs(p.controls)), []);

  const save = (patch: Partial<PadBindingPrefs>) => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({ ...cur, controls: { ...cur.controls, ...patch } });
  };
  const bind = (column: 'gamepadButtons' | 'gamepadShiftedButtons', button: PadAbilityButton, action: string) =>
    save({ [column]: { ...prefs[column], [button]: action } });
  const shift = prefs.gamepadShiftButton;
  const defaults = USER_PREFS_DEFAULTS.controls;

  return (
    <section className="controller-bindings" data-testid="controller-bindings" aria-label="Controller bindings">
      <div className="controller-bindings-head">
        <span className="controller-bindings-title">Shoulders &amp; triggers</span>
        <label className="controller-bindings-shift">
          Shift bumper
          <select value={shift} onChange={(e) => save({ gamepadShiftButton: e.target.value as PadBindingPrefs['gamepadShiftButton'] })}
            aria-label="Shift bumper" data-testid="controller-shift-button">
            <option value="none">None</option>
            <option value="ability1">Left Bumper (LB)</option>
            <option value="ability2">Right Bumper (RB)</option>
          </select>
        </label>
      </div>
      <table className="controller-bindings-table">
        <thead>
          <tr>
            <th className="controller-bindings-col-button">Button</th><th>Fires</th>
            {shift !== 'none' && <th>With {PAD_BUTTON_SHORT[shift]} held</th>}
          </tr>
        </thead>
        <tbody>
          {PAD_ABILITY_BUTTONS.map((button) => (
            <tr key={button} data-testid={`controller-binding-${button}`}>
              <th scope="row" title={PAD_BUTTON_LABELS[button]}>{PAD_BUTTON_SHORT[button]}</th>
              {button === shift ? (
                <td colSpan={2} className="controller-bindings-shift-note">Shift: hold for the second column</td>
              ) : (
                <>
                  <td><ActionSelect value={prefs.gamepadButtons[button]} onChange={(a) => bind('gamepadButtons', button, a)}
                    label={`${PAD_BUTTON_LABELS[button]} fires`} testId={`controller-bind-${button}`} /></td>
                  {shift !== 'none' && (
                    <td><ActionSelect value={prefs.gamepadShiftedButtons[button]} onChange={(a) => bind('gamepadShiftedButtons', button, a)}
                      label={`${PAD_BUTTON_LABELS[button]} with shift held fires`} testId={`controller-bind-shifted-${button}`} /></td>
                  )}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="controller-bindings-foot">
        <TomeButton tone="secondary" data-testid="controller-bindings-reset"
          onClick={() => save({ gamepadButtons: defaults.gamepadButtons, gamepadShiftedButtons: defaults.gamepadShiftedButtons, gamepadShiftButton: 'none' })}>
          Reset controller to defaults
        </TomeButton>
      </div>
    </section>
  );
};
